import { jsx } from 'react/jsx-runtime';
import { useContext, createContext, useMemo } from 'react';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import * as Portal from '@radix-ui/react-portal';
import { AnimatePresence } from 'framer-motion';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';

const FloatAnchorContext = /* @__PURE__ */ createContext(null);
const FloatAnchorRoot = ({ open: openProp, setOpen: setOpenProp, children, offset: offsetOptions = 5, middleware, useTransform = true }) => {
  const defaultOpenState = useBoolState();
  const open = useMemo(() => openProp ?? defaultOpenState.value, [
    openProp,
    defaultOpenState.value
  ]);
  const setOpen = useMemo(() => setOpenProp ?? defaultOpenState.set, [
    setOpenProp,
    defaultOpenState.set
  ]);
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [
      offset(offsetOptions),
      flip(),
      shift(),
      ...middleware ?? []
    ],
    whileElementsMounted: autoUpdate,
    transform: useTransform
  });
  return /* @__PURE__ */ jsx(FloatAnchorContext.Provider, {
    value: {
      open,
      setOpen,
      refs,
      floatingStyles
    },
    children
  });
};
const FloatAnchorTrigger = ({ children, action = "hover", asChild }) => {
  const context = useContext(FloatAnchorContext);
  if (!context) throw new Error("FloatAnchorTrigger must be used within FloatAnchorRoot");
  const { setOpen, open } = context;
  const handlers = useMemo(() => ({
    onMouseEnter: () => action === "hover" && setOpen(true),
    onMouseLeave: () => action === "hover" && setOpen(false),
    onFocus: () => action === "focus" && setOpen(true),
    onBlur: () => action === "focus" && setOpen(false),
    onClick: () => action === "click" && setOpen(!open)
  }), [
    action,
    setOpen,
    open
  ]);
  const Component = asChild ? Slot : "div";
  return /* @__PURE__ */ jsx(Component, {
    ref: context.refs.setReference,
    ...handlers,
    children
  });
};
const FloatAnchorFloating = ({ children, asChild }) => {
  const context = useContext(FloatAnchorContext);
  if (!context) throw new Error("FloatAnchorFloating must be used within FloatAnchorRoot");
  const Component = asChild ? Slot : "div";
  return /* @__PURE__ */ jsx(Portal.Root, {
    children: /* @__PURE__ */ jsx(Portal.Portal, {
      container: getDefaultRadixPortalContainer(),
      children: /* @__PURE__ */ jsx(AnimatePresence, {
        children: context.open && /* @__PURE__ */ jsx(Component, {
          ref: context.refs.setFloating,
          style: context.floatingStyles,
          "data-state": context.open ? "open" : "closed",
          children
        }, "floating")
      })
    })
  });
};
const FloatAnchor = {
  Root: FloatAnchorRoot,
  Trigger: FloatAnchorTrigger,
  Floating: FloatAnchorFloating
};

export { FloatAnchor };
//# sourceMappingURL=FloatAnchor.js.map
